import { useCallback, useEffect, useState } from 'react';
import { XrayRemoteControlRtcConnection, XrayRtcConnectionInfo } from '@bymason/controller-api';
import { GetRtcConnectionHook } from '@bymason/api-hooks-base';

export type XrayRemoteControlConnectionInfo = XrayRtcConnectionInfo;

/**
 * React hook that manages an X-Ray Remote-Control connection.
 * @returns HookArray
 * @HookArray [0] start: method that starts the connection
 * @HookArray [1] stop: method that stops the connection
 * @HookArray [2] dataChannel: connection's RTC data channel (or null)
 * @HookArray [3] connectionInfo: current connection info (or null)
 * @HookArray [4] timedOut: flag that indicates whether or not connection timed out
 * @HookArray [5] error: string (empty if there's no error)
 * @HookArray [6] busy: flag that indicates whether or not connection is in-process of starting
 */
export function useXrayRemoteControlConnection(deviceId: string, clientToken: string): GetRtcConnectionHook {
  const [connection, setConnection] = useState<XrayRemoteControlRtcConnection | null>(null);
  const [dataChannel, setDataChannel] = useState<RTCDataChannel | null>(null);
  const [connectionInfo, setConnectionInfo] = useState<XrayRemoteControlConnectionInfo | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleDataChannelOpen = useCallback((dc: RTCDataChannel) => {
    setDataChannel(dc);
  }, []);

  const handleError = useCallback((err: string) => {
    setError(err);
  }, []);

  const handleConnectionInfo = useCallback((info: XrayRemoteControlConnectionInfo) => {
    setConnectionInfo(info);
  }, []);

  const handleTimeout = useCallback(() => {
    setTimedOut(true);
  }, []);

  const start = useCallback(() => {
    setConnectionInfo(null);
    setDataChannel(null);
    setError('');
    setTimedOut(false);

    connection?.start();
  }, [connection]);

  const stop = useCallback(() => {
    connection?.stop();
  }, [connection]);

  useEffect(() => {
    setBusy(connectionInfo?.status === 'pending');
  }, [connectionInfo?.status]);

  useEffect(() => {
    if (clientToken) {
      const newConnection = new XrayRemoteControlRtcConnection({
        clientToken,
        deviceId,
        onDataChannelOpen: handleDataChannelOpen,
        onError: handleError,
        onReport: handleConnectionInfo,
        onConnectionStop: handleConnectionInfo,
        onTimeout: handleTimeout,
      });

      setConnection(newConnection);
    }
  }, [clientToken, deviceId, handleConnectionInfo, handleDataChannelOpen, handleError, handleTimeout]);

  return [start, stop, dataChannel, connectionInfo, timedOut, error, busy];
}
